import React, { useState } from "react";

/** Nextjs */
import dynamic from "next/dynamic";

/** MUI */
import {
    Box,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItemAvatar,
    ListItemButton,
    ListItemText,
    Paper,
    Typography,
    useMediaQuery,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
/** - icons */
import CloseIcon from "@mui/icons-material/Close";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import MoreVertIcon from "@mui/icons-material/MoreVert";

/** Redux */

/** Helpers */
import useHelper from "@/src/_common/useHelper";
import clsx from "clsx";
import { useTranslation } from "react-i18next";

/** UI */
const AppTooltip = dynamic(() => import("@/src/_common/AppTooltip"));

const useStyles = makeStyles((theme) => ({
    button: {
        [theme.breakpoints.down("sm")]: {
            // position: "absolute",
            // right: 0,
            // top: 0,
        },
    },
    drawerPaper: {
        ...theme.shape.boxedWrapper,
        borderRadius: "0 20px 20px 0",
        fontSize: "1rem",
        fontWeight: "normal",
        minWidth: 500,
        maxWidth: "calc(50vw)",
        boxShadow: theme.shadows[3],
        padding: theme.spacing(0, 1, 2, 2),
        [theme.breakpoints.down("lg")]: {
            padding: theme.spacing(1, 0),
            fontSize: "0.88888889rem",
        },
        [theme.breakpoints.down("md")]: {
            fontSize: "0.77777778rem",
        },
        [theme.breakpoints.down("sm")]: {
            maxWidth: "calc(100vw - 8px)",
            minWidth: "calc(100vw - 8px)",
        },
    },
    drawerPaperRight: {
        borderRadius: "20px 0 0 20px",
    },
    headWrap: {
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        marginBottom: theme.spacing(3),
        padding: theme.spacing(2, 0),
        position: "sticky",
        top: 0,
        backgroundColor: "#fff",
        [theme.breakpoints.down("lg")]: {
            padding: theme.spacing(1, 0),
            marginBottom: theme.spacing(0),
        },
    },
    title: {
        flexGrow: 1,
        textAlign: "center",
        fontSize: "1.33333333rem",
        fontWeight: 600,
        paddingRight: theme.spacing(2),
        [theme.breakpoints.down("xl")]: {
            fontSize: "1.11111111rem",
        },
        [theme.breakpoints.down("lg")]: {
            fontSize: "1rem",
        },
        [theme.breakpoints.down("md")]: {
            fontSize: "0.88888889rem",
        },
    },
    intro: {
        fontSize: "0.88888889rem",
        [theme.breakpoints.down("xl")]: {},
        [theme.breakpoints.down("lg")]: {
            fontSize: "0.88888889rem",
        },
        [theme.breakpoints.down("md")]: {
            fontSize: "0.77777778rem",
        },
    },
    moduleName: {
        color: theme.palette.primary.main,
        fontSize: "1rem",
        fontWeight: 500,
        [theme.breakpoints.down("lg")]: {
            fontSize: "0.88888889rem",
        },
    },
    moduleIntro: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap!important",
        fontSize: "0.77777778rem",
        [theme.breakpoints.down("lg")]: {},
    },
    goto: {
        color: theme.palette.primary.main,
        fontSize: "0.88888889rem",
        textDecoration: "underline",
        marginLeft: theme.spacing(2),
        [theme.breakpoints.down("lg")]: {
            fontSize: "0.77777778rem",
        },
    },
}));

const TableOfContents = (props) => {
    const { area, data } = props;

    const classes = useStyles();
    const { t } = useTranslation();
    const { translationFromJson } = useHelper();

    const downMd = useMediaQuery((theme) => theme.breakpoints.down("md"));
    const downLg = useMediaQuery((theme) => theme.breakpoints.down("lg"));

    /** States */
    const [SHOW_TOC, setSHOW_TOC] = useState(false);

    /** handlers */
    const handleShow = () => {
        setSHOW_TOC((prev) => !prev);
    };

    const handleScroll = (props) => {
        const { id } = props;

        handleShow();

        const _sticky_box = document.getElementById("sticky_box");
        const _top = _sticky_box?.getBoundingClientRect()?.height + (downLg ? 74 : 104);

        const _el = document.getElementById(id);
        const _offsetTop = _el?.offsetTop ? _el?.offsetTop - _top : 0;

        setTimeout(() => {
            const SCROLLBAR = document.querySelector("#appScroll > .os-viewport");
            SCROLLBAR?.scrollTo({ top: _offsetTop, behavior: "smooth" });
        }, 1);
    };

    return (
        <>
            {translationFromJson(data, "intro")?.length > 1 || data?.charts?.length || data?.modules?.length ? (
                <>
                    <AppTooltip
                        title={t("Otwórz informacje dodatkowe")}
                        arrow
                        placement={"top-end"}
                        type={"dark"}
                        classes={{ tooltip: classes.tooltip }}
                    >
                        <IconButton
                            onClick={handleShow}
                            color={"primary"}
                            sx={{ alignSelf: { xs: "flex-start", md: "center" } }}
                            className={clsx(classes.button)}
                        >
                            <MoreVertIcon />
                        </IconButton>
                    </AppTooltip>

                    <Drawer
                        anchor={downMd ? "right" : "left"}
                        open={SHOW_TOC}
                        onClose={handleShow}
                        classes={{ paper: clsx(classes.drawerPaper, downMd && classes.drawerPaperRight) }}
                        role={"dialog"}
                        aria-label={area}
                    >
                        <Box className={classes.headWrap} aria-hidden={true}>
                            <Typography variant={"body1"} className={classes.title}>
                                {area}
                            </Typography>

                            <AppTooltip title={t("Zamknij okno")} placement={"top"} type={"dark"}>
                                <IconButton onClick={handleShow} size={downLg ? "small" : "medium"} sx={{ mr: 1 }}>
                                    <CloseIcon fontSize={downLg ? "small" : "medium"} />
                                </IconButton>
                            </AppTooltip>
                        </Box>

                        <Paper elevation={0} sx={{ overflow: "auto", px: 1, pb: 5 }}>
                            {translationFromJson(data, "intro")?.length > 1 ? (
                                <Box>
                                    <Typography variant={"body1"} className={classes.intro} >
                                        {translationFromJson(data, "intro")}
                                    </Typography>
                                </Box>
                            ) : null}

                            {!!data?.modules?.length || !!data?.charts?.length ? (
                                <>
                                    <Typography variant={"body1"} sx={{ mt: 3, fontWeight: 500 }}>
                                        {t("Na tej stronie{{colon}}", { colon: ":" })}
                                    </Typography>

                                    <List dense>
                                        <ListItemButton
                                            onClick={() => handleScroll({ id: `back-to-top-anchor` })}
                                            alignItems={"flex-start"}
                                            sx={{ alignItems: "center" }}
                                            // aria-label={t(`przejdź do rozdziału "Dashboard" na tej stronie`)}
                                            aria-label={t(
                                                `przejdź do rozdziału "Dashboard". 
                                                Rozdział obejmuje{{colon}} widżety ({{widgetsCount}}) oraz wykresy ({{chartsCount}}){{dot}}`,
                                                {
                                                    colon: ":",
                                                    dot: ".",
                                                    widgetsCount: data.widgets?.length,
                                                    chartsCount: data.charts?.length,
                                                }
                                            )}
                                        >
                                            <ListItemText
                                                primary={t("Dashboard")}
                                                secondary={t("Widżety ({{widgetsCount}}); wykresy ({{chartsCount}})", {
                                                    colon: ":",
                                                    widgetsCount: data.widgets?.length,
                                                    chartsCount: data.charts?.length,
                                                })}
                                                classes={{
                                                    primary: classes.moduleName,
                                                    secondary: classes.moduleIntro,
                                                }}
                                            />
                                            <ListItemAvatar sx={{ alignSelf: "flex-end" }}>
                                                <Box component={"span"} className={classes.goto}>
                                                    <KeyboardDoubleArrowRightIcon />
                                                </Box>
                                            </ListItemAvatar>
                                        </ListItemButton>
                                        {/* <Divider /> */}

                                        {data?.modules?.map((mod) => {
                                            return (
                                                <React.Fragment key={mod.id}>
                                                    <ListItemButton
                                                        onClick={() => handleScroll({ id: `${mod.id}` })}
                                                        alignItems={"flex-start"}
                                                        sx={{ alignItems: "center" }}
                                                        aria-label={t(
                                                            `przejdź do rozdziału "${translationFromJson(
                                                                mod,
                                                                "title"
                                                            )}". Rozdział obejmuje{{colon}} informacje opisowe oraz wykresy({{count}}){{dot}}`,
                                                            {
                                                                colon: ":",
                                                                dot: ".",
                                                                count: mod.charts?.length,
                                                            }
                                                        )}
                                                    >
                                                        <ListItemText
                                                            primary={translationFromJson(mod, "title")}
                                                            secondary={t("Informacje opisowe; wykresy ({{count}})", {
                                                                colon: ":",
                                                                count: mod.charts?.length,
                                                            })}
                                                            classes={{
                                                                primary: classes.moduleName,
                                                                secondary: classes.moduleIntro,
                                                            }}
                                                        />
                                                        <ListItemAvatar sx={{ alignSelf: "flex-end" }}>
                                                            <Box component={"span"} className={classes.goto}>
                                                                <KeyboardDoubleArrowRightIcon />
                                                            </Box>
                                                        </ListItemAvatar>
                                                    </ListItemButton>
                                                    {/* <Divider /> */}
                                                </React.Fragment>
                                            );
                                        })}
                                    </List>
                                </>
                            ) : null}
                        </Paper>
                    </Drawer>
                </>
            ) : null}
        </>
    );
};

export default TableOfContents;
